/**
 * Task #7376 — guard against client-side redirect loops.
 *
 * Sentry DRIVESHARE_V2-E5: /ie/classic-car-hire-cork bounced between
 * /ie/en/classic-car-hire-cork and /ie/... because two URL normalisers
 * (LandingPageCatchAll's matchedPath canonicalisation and LocaleContext's
 * default-language strip) disagreed on the canonical form, each rewriting
 * the other's output via history.replaceState until Safari's rate limit
 * ("history.replaceState() more than 100 times per 10 seconds") threw a
 * SecurityError and crashed the ErrorBoundary.
 *
 * Every programmatic replace/push rewrite of the address bar should pass
 * through `allowHistoryRewrite(target)`. A given target URL may only be
 * written a small number of times per window; once exceeded the caller must
 * bail out (leaving a non-canonical-but-working URL is strictly better than
 * a crash loop).
 */

const WINDOW_MS = 10_000;
/** Max identical-target rewrites per window. 2 tolerates a legitimate
 * back-and-forth (e.g. user navigates away and back) while a real loop hits
 * the cap within milliseconds. */
const MAX_WRITES_PER_TARGET = 2;
/** Global cap across ALL targets per window. An A↔B oscillation keeps each
 * target under its individual cap while the combined write rate can still
 * reach the browser's 100/10s ceiling. This cap catches that case. */
const MAX_WRITES_GLOBAL = 15;

const recentWrites = new Map<string, number[]>();
let globalWriteStamps: number[] = [];

export function allowHistoryRewrite(target: string, now: number = Date.now()): boolean {
  // Per-target check.
  const stamps = (recentWrites.get(target) ?? []).filter((t) => now - t < WINDOW_MS);
  if (stamps.length >= MAX_WRITES_PER_TARGET) {
    recentWrites.set(target, stamps);
    if (typeof console !== "undefined") {
      // eslint-disable-next-line no-console
      console.warn(`[navigation-loop-guard] suppressed repeated history rewrite to ${target}`);
    }
    return false;
  }

  // Global total-writes check across all targets.
  globalWriteStamps = globalWriteStamps.filter((t) => now - t < WINDOW_MS);
  if (globalWriteStamps.length >= MAX_WRITES_GLOBAL) {
    recentWrites.set(target, stamps);
    if (typeof console !== "undefined") {
      // eslint-disable-next-line no-console
      console.warn(
        `[navigation-loop-guard] global write budget exhausted (${MAX_WRITES_GLOBAL}/${WINDOW_MS}ms), suppressing rewrite to ${target}`,
      );
    }
    return false;
  }

  stamps.push(now);
  recentWrites.set(target, stamps);
  globalWriteStamps.push(now);
  return true;
}

/**
 * Number of loop-guard-approved rewrites in the recent window. The search
 * page's `replaceStateIfChanged` (historyWrite.ts) folds this into its own
 * write budget so the COMBINED write rate — loop-guard-gated canonicalisation
 * rewrites plus search-page URL writers — stays under the browser's ~100/10s
 * history throttle even when both flood at once.
 */
export function recentLoopGuardWriteCount(now: number = Date.now()): number {
  globalWriteStamps = globalWriteStamps.filter((t) => now - t < WINDOW_MS);
  return globalWriteStamps.length;
}

/** Test helper. */
export function resetNavigationLoopGuard(): void {
  recentWrites.clear();
  globalWriteStamps = [];
}
